/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message, Button, Upload, Select, Spin, Checkbox, Card } from "antd";
import {
  CloudDownloadOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

const { Dragger } = Upload;

/* Simple icons used in original component */
const CheckMark = () => (
  <span style={{ color: "green", marginLeft: 6 }}>✓</span>
);
const StarIcon = () => <span style={{ color: "#faad14" }}>★</span>;
const DownloadIcon = ({ size = 40 }) => (
  <DownloadOutlined style={{ fontSize: size }} />
);

/* ------------------------- Mocked local data ------------------------- */
/* This mirrors the shape you used in the original component. */
const MOCK_MAPPER_FIELDS = {
  all_fields: [
    { key: "user_id", value: "user_id" },
    { key: "first_name", value: "first_name" },
    { key: "last_name", value: "last_name" },
    { key: "email", value: "email" },
    { key: "department", value: "Department" },
    { key: "entity", value: "Entity" },
    { key: "division", value: "Division" },
  ],
  mandatory: [
    { key: "User Id", value: "user_id" },
    { key: "First Name", value: "first_name" },
  ],
  non_mandatory: [
    { key: "Last Name", value: "last_name" },
    { key: "Email", value: "email" },
  ],
};

const MOCK_ORG_STRUCTURE = {
  department: "Engineering",
  entity: "Product",
  division: "Platform",
};

const capitalizeWords = (s) =>
  (s || "").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
const UploadInterface = () => {
  const { id } = useParams();
  const history = useNavigate();

  // UI state
  const [loader, setLoader] = useState(false);
  const [apiData, setApiData] = useState({}); // { name, columns: [] }
  // eslint-disable-next-line no-unused-vars
  const [mapperFields, setMapperFields] = useState(MOCK_MAPPER_FIELDS);
  const [orgStructure] = useState(MOCK_ORG_STRUCTURE);
  const [mapper, setMapper] = useState({}); // { columnName: mappedValue }
  const [checkedFields, setCheckedFields] = useState([]); // selected mapped values
  const [autoMapCheck, setAutoMapCheck] = useState(false);
  const [AutoMap, setAutoMap] = useState(false);
  const default_app = localStorage.getItem("default_app") || "Trak Learning";

  // derive given array of allowed mapping keys (like original)
  const [givenArray, setGivenArray] = useState([]);
  useEffect(() => {
    const combined = [
      ...(mapperFields?.mandatory || []).map((m) => m.value),
      ...(mapperFields?.non_mandatory || []).map((n) => n.value),
    ];
    setGivenArray(combined);
  }, [mapperFields]);

  /* --------------- Fake upload handling ---------------
     We do not read the file. We simply set mock columns to demonstrate UI.
     If you want to actually parse Excel on client, integrate xlsx library here.
  ---------------------------------------------------- */
  const uploadImage = ({ file, onSuccess }) => {
    setLoader(true);
    // fake delay to show spinner
    setTimeout(() => {
      // create fake columns from filename or a default set
      const baseName = file?.name?.split(".")[0] || "uploaded_file";
      const columnsFromName = baseName
        .split(/[_\-\s]+/)
        .slice(0, 4)
        .map((p, i) => (i === 0 ? "user_id" : p.toLowerCase()));
      // fallback columns
      const columns =
        columnsFromName.length >= 2
          ? columnsFromName
          : ["user_id", "first_name", "email", "department"];

      setApiData({
        id: "local-" + Date.now(),
        name: file?.name || "uploaded.xlsx",
        columns,
      });

      setLoader(false);
      if (onSuccess) onSuccess("ok");
      message.success("File uploaded (mock)");
    }, 700);
  };

  const draggerprops = {
    name: "file",
    multiple: false,
    accept:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel",
    customRequest: uploadImage,
    beforeUpload: (file) => {
      // allow any extension for demo but give a warning if not xlsx
      const ok =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name?.toLowerCase().endsWith(".xlsx") ||
        file.name?.toLowerCase().endsWith(".xls");
      if (!ok) {
        message.warn(
          `${file.name} may not be an Excel file (allowed: .xlsx/.xls)`
        );
      }
      return true; // let customRequest handle it
    },
  };

  /* Cancel: just navigate back to uploads list (mimic original paths) */
  const cancelUploadProcess = () => {
    const isLearning = default_app === "Trak Learning";
    if (isLearning) {
      history(`/track/customers/learners/uploads/${id}`);
    } else {
      history(`/track-coaching/customers/learners/uploads/${id}`);
    }
  };

  /* When user picks mapping for a column */
  const setMapperData = (col, val) => {
    const next = { ...mapper };
    if (!val) {
      delete next[col];
    } else {
      next[col] = val;
    }
    setMapper(next);

    // update checkedFields: values currently used in mapper
    const used = Object.values(next).filter(Boolean);
    setCheckedFields(used);
  };

  /* Simple validation: ensure mandatory mapped */
  const validate = () => {
    const mandatoryCheck = ["user_id", "first_name"];
    return mandatoryCheck.some((f) => !Object.values(mapper).includes(f));
  };

  /* AutoMap detection: checks if any column matches givenArray (case-insensitive) */
  useEffect(() => {
    if (apiData?.columns && givenArray.length > 0) {
      const normalizedGiven = givenArray.map((g) =>
        g.toLowerCase().replace(/\s+/g, "")
      );
      const columnsMatch = apiData.columns.some((c) =>
        normalizedGiven.includes(c.toLowerCase().replace(/\s+/g, ""))
      );
      setAutoMap(columnsMatch);
    } else {
      setAutoMap(false);
    }
  }, [apiData?.columns, givenArray]);

  /* When autoMapCheck toggles ON, perform a best-effort mapping */
  useEffect(() => {
    if (AutoMap && apiData?.columns && autoMapCheck) {
      const next = {};
      apiData.columns.forEach((col) => {
        // find a field in all_fields whose key/value matches the column (case-insensitive)
        const match =
          mapperFields.all_fields.find(
            (f) =>
              f.value.toLowerCase() === col.toLowerCase() ||
              f.key.toLowerCase() === col.toLowerCase()
          ) || null;
        if (match) next[col] = match.value;
      });
      setMapper(next);
      setCheckedFields(Object.values(next));
    } else if (!autoMapCheck) {
      // if turned off, clear mapper but keep apiData
      // (mirrors original behavior where auto map toggles clear mappings)
      setMapper({});
      setCheckedFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMapCheck, AutoMap, apiData?.columns]);

  /* Submit: fake complete and navigate to details route */
  const handleSubmit = () => {
    if (validate()) {
      message.error("Please map mandatory fields before submitting.");
      return;
    }
    setLoader(true);
    setTimeout(() => {
      message.success("Fields mapped successfully (mock).");
      history(`/track/customers/learners/upload-details/${id}/${apiData.id}`);
      setLoader(false);
    }, 500);
  };

  /* Utility to render option text (use orgStructure for Department/Entity/Division) */
  const optionLabel = (field) => {
    if (field.key === "Department") return orgStructure.department;
    if (field.key === "Entity") return orgStructure.entity;
    if (field.key === "Division") return orgStructure.division;
    return field.key;
  };

  return (
    <div>
      <div className="tableCourses" style={{ padding: 16 }}>
        {loader && (
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <Spin />
          </div>
        )}

        <div
          className="track-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {capitalizeWords(
                default_app === "Trak Coaching"
                  ? "NEW COACHEE EXCEL UPLOAD"
                  : "NEW LEARNERS EXCEL UPLOAD"
              )}
            </div>
          </div>
          <div>
            <a
              className="button report-button padd"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                message.info("Download template (mock)");
              }}
            >
              <CloudDownloadOutlined /> Download File Format
            </a>
          </div>
        </div>

        <div
          className="upload-panel"
          style={{ display: "flex", gap: 16, marginTop: 16 }}
        >
          {/* Left: file upload */}
          <div className="file-section" style={{ width: "40%" }}>
            <div style={{ marginBottom: 12 }}>
              {!apiData?.name && (
                <Upload {...draggerprops} showUploadList={false}>
                  <Button icon={<UploadOutlined />}>Upload manually</Button>
                </Upload>
              )}
            </div>

            <div className="dragger" style={{ marginBottom: 12 }}>
              {!apiData?.name ? (
                <Dragger {...draggerprops} showUploadList={false}>
                  <p className="ant-upload-drag-icon">
                    <DownloadIcon size={48} />
                  </p>
                  <p className="ant-upload-text">Drop File to upload</p>
                  <p className="ant-upload-hint">.xlsx .xls</p>
                </Dragger>
              ) : (
                <Card>
                  <p style={{ fontWeight: 600 }}>{apiData?.name}</p>
                  <p style={{ marginBottom: 8 }}>
                    is selected for field mapping
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      onClick={() => {
                        setApiData({});
                        setMapper({});
                        setCheckedFields([]);
                      }}
                    >
                      Remove File
                    </Button>
                    <Button onClick={() => message.info("Preview (mock)")}>
                      Preview
                    </Button>
                  </div>
                </Card>
              )}
            </div>

            <div style={{ textAlign: "center" }}>
              <Button onClick={cancelUploadProcess}>Cancel Upload</Button>
            </div>
          </div>

          {/* Right: field mapper */}
          <div className="field-mapper-section" style={{ flex: 1 }}>
            <div className="header-section" style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ fontWeight: 600 }}>Field Mapping</div>
                {AutoMap && (
                  <div style={{ fontSize: 14 }}>
                    Auto MAP{" "}
                    <Checkbox
                      checked={autoMapCheck}
                      onChange={(e) => setAutoMapCheck(e.target.checked)}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <div style={{ flex: 3, fontWeight: 500 }}>
                  Source file columns
                </div>
                <div style={{ flex: 2, fontWeight: 500 }}>Mapped columns</div>
              </div>
            </div>

            <div
              className="data-section"
              style={{ border: "1px solid #eee", borderRadius: 6, padding: 12 }}
            >
              <div style={{ display: "flex", gap: 16 }}>
                <div className="cols src-cols" style={{ flex: 3 }}>
                  {apiData?.columns ? (
                    apiData.columns.map((col, i) => (
                      <div
                        key={`mapper-column-${i}`}
                        style={{
                          display: "flex",
                          gap: 12,
                          padding: "8px 0",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            background: i % 2 === 0 ? "#fafafa" : "transparent",
                            borderRadius: 4,
                          }}
                        >
                          {col}
                        </div>

                        <div style={{ flex: 1 }}>
                          <Select
                            value={mapper[col]}
                            placeholder="Select mapping column"
                            style={{ width: "100%" }}
                            onChange={(val) => setMapperData(col, val)}
                            allowClear
                          >
                            {mapperFields?.all_fields?.map((field, fi) => (
                              <Select.Option
                                value={field.value}
                                key={`mapper-selector-${fi}`}
                                disabled={
                                  checkedFields.includes(field.value) &&
                                  mapper[col] !== field.value
                                }
                              >
                                {optionLabel(field)}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: 16 }}>
                      <div
                        style={{
                          padding: 8,
                          background: "#fafafa",
                          borderRadius: 4,
                        }}
                      >
                        --
                      </div>
                    </div>
                  )}
                </div>

                <div className="mandatory-field-indication" style={{ flex: 1 }}>
                  <p style={{ fontWeight: 700, marginBottom: 8 }}>
                    Mandatory Fields <StarIcon />
                  </p>

                  {mapperFields?.mandatory?.map((man, i) => (
                    <div
                      key={`mandatory-field-${i}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "6px 0",
                      }}
                    >
                      <div style={{ flex: 1 }}>{man.key}</div>
                      <div>
                        {Object.values(mapper).includes(man.value) ? (
                          <CheckMark />
                        ) : null}
                      </div>
                    </div>
                  ))}

                  <p style={{ fontWeight: 700, marginTop: 12 }}>
                    Optional Fields
                  </p>

                  {mapperFields?.non_mandatory?.map((nman, i) => (
                    <div
                      key={`optional-field-${i}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "6px 0",
                      }}
                    >
                      <div style={{ flex: 1 }}>{nman.key}</div>
                      <div>
                        {Object.values(mapper).includes(nman.value) ? (
                          <CheckMark />
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, textAlign: "right" }}>
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={validate() || !apiData?.id}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadInterface;
